// ** React Imports
import React, { Fragment, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";

// ** Third Party Components
import PropTypes from "prop-types";
import classnames from "classnames";
import { AlertCircle, Loader, X } from "react-feather";
import PerfectScrollbar from "react-perfect-scrollbar";

const MUDLogoLight =
    require("@src/assets/images/the-club/factions/mud_white.svg").default;
const USTURLogoLight =
    require("@src/assets/images/the-club/factions/ustur_white.svg").default;
const ONILogoLight =
    require("@src/assets/images/the-club/factions/oni_white.svg").default;
const logo = require("@src/assets/images/the-club/logo/ATLAS.png").default;

// ** Custom Hooks
import { useOnClickOutside } from "@hooks/useOnClickOutside";

// ** Styles
import "@styles/base/bootstrap-extended/_include.scss";
import "./search-input.scss";
import { Badge } from "reactstrap";
import { useSkin } from "../../../utility/hooks/useSkin";

const SearchInput = (props) => {
    // ** Refs
    const container = useRef(null);
    const inputElRef = useRef(null);
    const suggestionsListRef = useRef(null);

    const [skin] = useSkin();

    // ** States
    const [focused, setFocused] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(0);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [userInput, setUserInput] = useState(props.value ? props.value : "");

    // ** Vars
    const history = useHistory();

    // ** Suggestion Item Click Event
    const onSuggestionItemClick = (suggestion, e) => {
        const url = suggestion.address
            ? `/wallet/${suggestion.address}`
            : `/asset/${suggestion.mint}`;
        setActiveSuggestion(0);
        setShowSuggestions(false);
        if (url !== undefined && url !== null) {
            history.push(url);
        }
    };

    // ** Suggestion Hover Event
    const onSuggestionItemHover = (index) => {
        setActiveSuggestion(index);
    };

    // ** Input On Change Event
    const onChange = (e) => {
        const userInput = e.currentTarget.value;
        setActiveSuggestion(0);
        setShowSuggestions(true);
        setUserInput(userInput);
        if (e.target.value < 1) {
            setShowSuggestions(false);
        }
    };

    // ** Input Click Event
    const onInputClick = (e) => {
        e.stopPropagation();
    };

    // ** Input's Keydown Event
    const onKeyDown = (e) => {
        const filterKey = props.filterKey;
        const suggestionList = ReactDOM.findDOMNode(suggestionsListRef.current);

        // ** User pressed the up arrow
        if (e.keyCode === 38 && activeSuggestion !== 0) {
            setActiveSuggestion(activeSuggestion - 1);

            if (
                e.target.value.length > -1 &&
                suggestionList !== null &&
                activeSuggestion <= suggestionList.length / 2
            ) {
                suggestionList.scrollTop = 0;
            }
        } else if (
            e.keyCode === 40 &&
            activeSuggestion < suggestionList.length - 1
        ) {
            // ** User pressed the down arrow
            setActiveSuggestion(activeSuggestion + 1);

            if (
                e.target.value.length > -1 &&
                suggestionList !== null &&
                activeSuggestion >= suggestionList.length / 2
            ) {
                suggestionList.scrollTop = suggestionList.scrollHeight;
            }
        } else if (e.keyCode === 27) {
            // ** User Pressed ESC
            setShowSuggestions(false);
            setUserInput("");
        } else if (e.keyCode === 13 && showSuggestions) {
            // ** User Pressed ENTER
            onSuggestionItemClick(suggestionList[activeSuggestion], e);
            setUserInput(suggestionList[activeSuggestion][filterKey]);
            setShowSuggestions(false);
        } else {
            return;
        }

        // ** Custom Keydown Event
        if (props.onKeyDown !== undefined && props.onKeyDown !== null) {
            props.onKeyDown(e, userInput);
        }
    };

    // ** Function To Render Grouped Suggestions
    // const renderGroupedSuggestion = arr => {
    //
    //   const renderSuggestion = (item, i) => {
    //     const suggestionURL = item.link !== undefined && item.link !== null ? item.link : null
    //     return (
    //       <li
    //         className={classnames('suggestion-item', {
    //           active: suggestions.indexOf(item) === activeSuggestion
    //         })}
    //         key={item.address}
    //         onClick={e => onSuggestionItemClick(suggestionURL, e)}
    //         onMouseEnter={() => {
    //           onSuggestionItemHover(suggestions.indexOf(item))
    //         }}
    //       >
    //         {item.address}
    //       </li>
    //     )
    //   }
    //
    //   return arr.map((item, i) => {
    //     return renderSuggestion(item, i)
    //   })
    // }

    const renderFaction = (faction) => {
        let bgColor = null;
        let src = null;
        switch (faction) {
            case "MUD":
                bgColor = "#e06a5d";
                src = MUDLogoLight;
                break;
            case "USTUR":
                bgColor = "#dfb968";
                src = USTURLogoLight;
                break;
            case "ONI":
                bgColor = "#6060d8";
                src = ONILogoLight;
                break;
        }
        return (
            <img
                className="address-img"
                src={src}
                alt={faction}
                height="20px"
                style={{ backgroundColor: bgColor }}
            />
        );
    };

    const renderUserImg = (avatarId) => {
        let avatar = logo;
        let alt = "Logo";
        // if (avatarId) {
        //   avatar = require(`@src/assets/images/the-club/wallet/avatars/${avatarId}_A.jpg`).default;
        //   alt = faction;
        // }
        return (
            <img
                src={avatar}
                alt={alt}
                className={`img-fluid rounded-circle ${
                    skin === "light" ? "avatar-border" : ""
                }`}
                height="200"
                width="200"
            />
        );
    };

    const numberWithCommas = (n) => {
        const parts = n.toString().split(".");
        return (
            parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
            (parts[1] ? `.${parts[1]}` : "")
        );
    };

    // ** Function To Render Ungrouped Suggestions
    const renderSuggestions = () => {
        const { filterKey, suggestions } = props;
        if (suggestions.length) {
            return suggestions.map((suggestion, index) => {
                return (
                    <li
                        className={classnames("suggestion-item", {
                            active:
                                suggestions.indexOf(suggestion) ===
                                activeSuggestion,
                        })}
                        key={index}
                        onClick={(e) => onSuggestionItemClick(suggestion, e)}
                        onMouseEnter={() =>
                            onSuggestionItemHover(
                                suggestions.indexOf(suggestion)
                            )
                        }
                    >
                        {suggestion.address ? (
                            <>
                                <div className="position-relative">
                                    <Badge
                                        pill
                                        className="zindex-1 search-badge"
                                    >
                                        <div
                                            style={{
                                                background:
                                                    suggestion.faction === "MUD"
                                                        ? "#e06a5d"
                                                        : suggestion.faction ===
                                                          "ONI"
                                                        ? "#6060d8"
                                                        : "#dfb968",
                                                borderRadius: "50%",
                                                // margin: '0px 0px 16px 16px',
                                                width: "10px",
                                                padding: "6px",
                                                height: "auto",
                                                border: "2px solid #666",
                                            }}
                                        />
                                    </Badge>
                                    {renderUserImg(suggestion.faction)}
                                </div>
                                <div className="suggestion-values">
                                    <div>{suggestion.address}</div>
                                    <span>
                                        Value:{" "}
                                        {suggestion.tokens_value
                                            ? suggestion.tokens_value.toLocaleString(
                                                  "en-US",
                                                  {
                                                      maximumFractionDigits: 0,
                                                  }
                                              )
                                            : "0"}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                {suggestion.image ? (
                                    <img
                                        src={`https://meta.theclubguild.com/thumb/?url=${suggestion.image}&size_x=64&size_y=64`}
                                        alt="Img"
                                        height="20px"
                                    />
                                ) : (
                                    <div className="empty-icon" />
                                )}
                                <div className="suggestion-values ">
                                    <div>
                                        {suggestion.name} ({suggestion.symbol})
                                    </div>
                                    <span className="d-inline text-truncate">{`Mint: ${suggestion.mint}`}</span>
                                </div>
                            </>
                        )}
                    </li>
                );
            });
        } else {
            return (
                <li className="suggestion-item no-result">
                    <AlertCircle size={15} />{" "}
                    <span className="align-middle ml-50">No Result</span>
                </li>
            );
        }
    };

    const clearInput = () => {
        setUserInput("");
        setShowSuggestions(false);
        props.clearInput();
    };

    //** ComponentDidMount

    useEffect(() => {
        if (props.defaultSuggestions && focused) {
            setShowSuggestions(true);
        }
    }, [focused, props.defaultSuggestions]);

    //** ComponentDidUpdate
    useEffect(() => {
        const textInput = ReactDOM.findDOMNode(inputElRef.current);

        // ** For searchbar focus
        if (textInput !== null && props.autoFocus) {
            inputElRef.current.focus();
        }

        // ** If user has passed default suggestions & focus then show default suggestions
        if (props.defaultSuggestions && focused) {
            setShowSuggestions(true);
        }

        // ** Function to run on user passed Clear Input
        // if (props.clearInput) {
        //   props.clearInput(userInput, setUserInput)
        // }

        // ** Function on Suggestions Shown
        if (props.onSuggestionsShown && showSuggestions) {
            props.onSuggestionsShown(userInput);
        }
    }, [setShowSuggestions, focused, userInput, showSuggestions, props]);

    // ** On External Click Close The Search & Call Passed Function
    useOnClickOutside(container, () => {
        setShowSuggestions(false);
        if (props.externalClick) {
            props.externalClick();
        }
    });

    let suggestionsListComponent;

    if (showSuggestions) {
        suggestionsListComponent = (
            <PerfectScrollbar
                className={classnames("suggestions-list", {
                    [props.wrapperClass]: props.wrapperClass,
                })}
                ref={suggestionsListRef}
                component="ul"
                options={{ wheelPropagation: false }}
            >
                {renderSuggestions()}
            </PerfectScrollbar>
        );
    }

    return (
        <div className="search-container" ref={container}>
            <div className="autocomplete-input-group d-flex">
                <div
                    className={`autocomplete-search form-control ${
                        props.className ? props.className : ""
                    }`}
                >
                    <input
                        id="q"
                        type="text"
                        onChange={(e) => {
                            onChange(e);
                            if (props.onChange) {
                                props.onChange(e);
                            }
                        }}
                        onKeyDown={(e) => onKeyDown(e)}
                        value={userInput}
                        placeholder={props.placeholder}
                        onClick={onInputClick}
                        ref={inputElRef}
                        onFocus={(e) => setFocused(true)}
                        autoFocus={props.autoFocus}
                        onBlur={(e) => {
                            if (props.onBlur) props.onBlur(e);
                            setFocused(false);
                        }}
                        style={{ padding: "2px 0px", fontSize: "18px" }}
                        autoComplete="off"
                    />
                </div>
                {props.loading && (
                    <span className="loader-icon">
                        <Loader size={16} className="ml-50" />
                    </span>
                )}
                <span
                    onClick={clearInput}
                    className="input-suffix close-icon"
                    style={{
                        marginBottom: "4px",
                    }}
                >
                    <X size={16} />
                </span>
            </div>
            {suggestionsListComponent}
        </div>
    );
};

export default SearchInput;

// ** PropTypes
SearchInput.propTypes = {
    suggestions: PropTypes.array.isRequired,
    filterKey: PropTypes.string,
    loading: PropTypes.bool,
    defaultValue: PropTypes.string,
    wrapperClass: PropTypes.string,
    filterHeaderKey: PropTypes.string,
    placeholder: PropTypes.string,
    suggestionLimit: PropTypes.number,
    grouped: PropTypes.bool,
    autoFocus: PropTypes.bool,
    onKeyDown: PropTypes.func,
    onChange: PropTypes.func,
    onSuggestionsShown: PropTypes.func,
    onSuggestionItemClick: PropTypes.func,
    clearInput: PropTypes.func,
    externalClick: PropTypes.func,
};
